import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";
import { Sparkles, Trash2, RefreshCw } from "lucide-react";

function PageHeader({ overline, title }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream">
      <div className="overline text-gold-deep">{overline}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
      <div className="gold-rule mt-4" />
    </div>
  );
}

export default function DemoManager() {
  const [busy, setBusy] = useState(null);
  const [counts, setCounts] = useState(null);

  const reseed = async () => {
    if (!(await confirmDialog({
      title: "Re-seed demo data?",
      description: "All current demo records will be deleted and replaced with the bundled starter dataset (4 contacts, 4 deals, 5 tasks, 8 investors, 3 endorsements, etc.). Your real CRM data is untouched.",
      confirmLabel: "Re-seed",
    }))) return;
    setBusy("seed");
    try {
      const { data } = await api.post("/demo/seed");
      setCounts(data.counts);
      toast.success("Demo data re-seeded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Re-seed failed");
    } finally {
      setBusy(null);
    }
  };

  const wipe = async () => {
    if (!(await confirmDialog({
      title: "Wipe all demo data?",
      description: "This deletes every demo record (contacts, deals, tasks, investors, outreach, endorsements, files, intros). Your real CRM data is untouched. You can re-seed any time.",
      confirmLabel: "Wipe",
      destructive: true,
    }))) return;
    setBusy("reset");
    try {
      const { data } = await api.post("/demo/reset");
      setCounts(null);
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Wiped ${total} demo records`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Wipe failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="demo-manager-page">
      <PageHeader overline="Demo Mode" title="Manage demo data" />
      <div className="p-8 max-w-3xl space-y-6">
        <div className="bg-white border border-navy/10 p-6">
          <h2 className="font-serif text-xl text-navy">You're signed in as the demo user.</h2>
          <p className="text-sm text-navy/70 font-light leading-relaxed mt-3">
            Every action you take here is isolated. Adding contacts, logging outreach, accepting consultations —
            none of it touches your real CRM. Use this account to explore every flow, train new users, or
            screen-share with clients without leaking real data.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={reseed}
              disabled={busy !== null}
              data-testid="demo-reseed"
              className="bg-navy text-gold px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-navy-600 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className="h-3.5 w-3.5" /> {busy === "seed" ? "Seeding…" : "Re-seed starter dataset"}
            </button>
            <button
              onClick={wipe}
              disabled={busy !== null}
              data-testid="demo-wipe"
              className="border border-destructive text-destructive px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50 flex items-center gap-2"
            >
              <Trash2 className="h-3.5 w-3.5" /> Wipe all demo data
            </button>
          </div>
        </div>

        {counts && (
          <div className="bg-cream border border-navy/10 p-6" data-testid="demo-counts">
            <div className="overline text-gold-deep mb-3 flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> Last seed summary
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(counts).map(([k, v]) => (
                <div key={k} className="bg-white border border-navy/10 p-3">
                  <div className="overline text-navy/55">{k}</div>
                  <div className="font-serif text-2xl text-navy">{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-cream border-l-2 border-gold p-4 text-xs text-navy/65 font-light">
          <strong className="text-navy">Tip:</strong> if you want to walk a client through the platform live,
          re-seed first so the dataset always looks complete. Demo data lives forever until you wipe it.
        </div>
      </div>
    </div>
  );
}
